// Bar ↔ in-room QR menu: show price differences, push POS prices to the menu, add new bar products to the menu.
import React, { useCallback, useEffect, useState } from 'react';
import { QrCode, Loader2, ArrowRight, Plus } from 'lucide-react';
import { apiCall } from '../_shared';
import { money } from './BarTab';

const DiffRow = ({ r }) => (
  <li className="flex flex-wrap items-center gap-x-2 py-0.5 border-t text-xs" data-testid={`menu-diff-${r.sku}`}>
    <span className="font-medium text-slate-900">{r.menu_name}</span>
    <span className="text-slate-400 line-through">{money(r.menu_price)}</span><ArrowRight className="w-3 h-3 text-slate-400" />
    <span className="font-semibold text-orange-600">{money(r.bar_price)}</span>
    {!r.menu_active && <span className="text-[10px] text-slate-400">(hidden on menu)</span>}
  </li>
);

export const MenuSync = ({ canManage, reload }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => { try { setData(await apiCall('/api/admin/bar/menu-sync')); } catch (e) { setMsg(e.message); } }, []);
  useEffect(() => { load(); }, [load]);
  const run = async (body) => {
    setBusy(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/bar/menu-sync', { method: 'POST', body: JSON.stringify(body) });
      setData(r); setSel({});
      setMsg(`${r.pushed.length} price${r.pushed.length === 1 ? '' : 's'} pushed · ${r.added.length} item${r.added.length === 1 ? '' : 's'} added to the menu${r.added.length ? ` · ${r.translated} translated by AI into the guest languages` : ''}.`);
      reload?.();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const s = data?.summary;
  const diffs = (data?.linked || []).filter((r) => !r.in_sync).map((r) => <DiffRow key={r.sku} r={r} />);
  const selected = Object.keys(sel).filter((k) => sel[k]);
  const missing = (data?.not_on_menu || []).map((r) => (
    <label key={r.product_id} className="flex items-center gap-2 py-0.5 border-t text-xs" data-testid={`menu-missing-${r.product_id}`}>
      <input type="checkbox" checked={!!sel[r.product_id]} disabled={!canManage} onChange={(e) => setSel((p) => ({ ...p, [r.product_id]: e.target.checked }))} data-testid={`menu-add-${r.product_id}`} />
      <span className="font-medium text-slate-900">{r.name}</span><span className="text-slate-500">{money(r.bar_price)} · {r.section}</span>
    </label>
  ));
  const menuOnly = (data?.menu_only || []).map((m) => <span key={m.sku} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 text-[11px] text-slate-600 mr-1 mb-1">{m.name} {money(m.price)}</span>);
  return (
    <details className="bg-white border rounded-xl p-3" data-testid="bar-menu-sync">
      <summary className="text-xs font-semibold text-slate-700 cursor-pointer inline-flex items-center gap-1">
        <QrCode className="w-3.5 h-3.5" /> In-room QR menu prices{s ? ` (${s.in_sync} in sync · ${s.differ} differ · ${s.not_on_menu} not on menu)` : ''}
        {s?.differ > 0 && <span className="ml-1 bg-orange-500 text-white text-[10px] rounded-full px-1.5" data-testid="menu-sync-badge">{s.differ}</span>}
      </summary>
      <p className="text-[11px] text-slate-500 mt-2">Guests see the POS price: every price change in Bar is mirrored to the linked QR-menu item automatically. Use this panel to fix old differences and to put new bar products on the menu — new items are translated by AI into ME/SQ/DE/RU/PL/HU automatically. Guest QR orders of bar products reduce bar stock and appear in Today as "Room order (QR)".</p>
      {msg && <p className="text-xs text-slate-700 mt-1" data-testid="menu-sync-msg">{msg}</p>}
      <div className="grid md:grid-cols-2 gap-3 mt-2">
        <div>
          <div className="flex items-center justify-between"><h4 className="text-xs font-semibold text-slate-800">Price differences ({diffs.length})</h4>
            {canManage && diffs.length > 0 && <button type="button" onClick={() => run({ push_prices: true })} disabled={busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-2 py-1 text-[11px] font-semibold disabled:opacity-50" data-testid="menu-push-prices">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />} Push POS prices to menu</button>}
          </div>
          {diffs.length ? <ul className="mt-1">{diffs}</ul> : <p className="text-xs text-emerald-700 mt-1" data-testid="menu-all-synced">All linked items show the POS price.</p>}
        </div>
        <div>
          <div className="flex items-center justify-between"><h4 className="text-xs font-semibold text-slate-800">Bar products not on the menu ({missing.length})</h4>
            {canManage && missing.length > 0 && <button type="button" onClick={() => run({ push_prices: false, add_product_ids: selected })} disabled={busy || !selected.length} className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded px-2 py-1 text-[11px] font-semibold disabled:opacity-50" data-testid="menu-add-to-menu"><Plus className="w-3 h-3" /> Add {selected.length || ''} to menu</button>}
          </div>
          {missing.length ? <div className="mt-1">{missing}</div> : <p className="text-xs text-slate-400 mt-1">Every sold bar product is on the QR menu.</p>}
        </div>
      </div>
      {menuOnly.length > 0 && <p className="text-[11px] text-slate-500 mt-3">Menu-only items (priced in the menu editor, no bar stock): {menuOnly}</p>}
    </details>
  );
};

export default MenuSync;
